import { DataTable } from "@/components/data-table";
import { HeaderCell } from "@/components/header-cell";
import { cn, formatCurrency } from "@/lib/utils";
import type { TradeHistory as TradeHistoryType, FillType } from "./types";
import {
  createColumnHelper,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import { useMemo, memo } from "react";
import { useNavigate } from "react-router-dom";
import { useProduct } from "@/features/product/product-context";
import { useProductOptions } from "@/features/product/product-options";
import { useMarketPrecision } from "@/hooks/use-price-precision";
import { useI18n } from "@/features/i18n";

// Helper to get display properties for each fill type
const getFillTypeDisplay = (fillType: FillType): { label: string; className: string } => {
  switch (fillType) {
    case "open":
      return { label: "Open", className: "text-muted-foreground" };
    case "partial_close":
      return { label: "Partial Close", className: "text-yellow-500" };
    case "full_close":
      return { label: "Full Close", className: "text-bid-foreground" };
    case "flip":
      return { label: "Flip", className: "text-purple-400" };
    case "liquidation":
      return { label: "Liquidated", className: "text-ask-foreground" };
    default:
      return { label: "Open", className: "text-muted-foreground" };
  }
};

// Price cell with dynamic market precision
const PriceCell = memo(function PriceCell({ 
  price, 
  market 
}: { 
  price: number; 
  market: string;
}) {
  const { quotePrecision } = useMarketPrecision(market);
  return (
    <div className="text-right font-medium">
      ${formatCurrency(price, quotePrecision)}
    </div>
  );
});

type TradeHistoryProps = {
  data: TradeHistoryType[];
  isLoading: boolean;
  sorting: SortingState;
  onSort: (state: SortingState) => void;
};

export const TradeHistory = ({
  data,
  isLoading,
  sorting,
  onSort,
}: TradeHistoryProps) => {
  const columns = useColumns();

  return (
    <DataTable
      columns={columns as ColumnDef<TradeHistoryType, unknown>[]}
      data={data}
      isLoading={isLoading}
      sorting={sorting}
      onSort={onSort}
    />
  );
};

const columnHelper = createColumnHelper<TradeHistoryType>();

const useColumns = () => {
  const { setProduct } = useProduct();
  const { products } = useProductOptions();
  const navigate = useNavigate();
  const { formatTradeDate } = useI18n();

  const columns = useMemo(
    () => [
      columnHelper.accessor("timestamp", {
        header: (h) => <HeaderCell header={h}>Time</HeaderCell>,
        cell: (props) => {
          const value = props.getValue();
          return (
            <div className="text-muted-foreground text-xs">
              {formatTradeDate(value)}
            </div>
          );
        },
      }),
      columnHelper.accessor("market", {
        header: (h) => <HeaderCell header={h}>Coin</HeaderCell>,
        cell: (props) => {
          const value = props.getValue();
          
          const handleMarketClick = () => {
            const selectedProduct = products.find((p) => p.id === value);
            if (selectedProduct) {
              setProduct(selectedProduct);
              // Navigate to new market URL (replace history to avoid back button cycling through markets)
              navigate(`/trade/${selectedProduct.id}`, { replace: true });
            }
          };
          
          return (
            <div className="flex items-center gap-2">
              <span 
                className="font-medium cursor-pointer hover:text-primary hover:underline transition-colors"
                onClick={handleMarketClick}
              >
                {value}
              </span>
            </div>
          );
        },
      }),
      columnHelper.accessor("side", {
        header: (h) => <HeaderCell header={h}>Direction</HeaderCell>,
        cell: (props) => {
          const value = props.getValue();
          const isLong = value === "long";
          return (
            <div
              className={cn(
                "font-medium",
                isLong ? "text-bid-foreground" : "text-ask-foreground"
              )}
            >
              {isLong ? "Long" : "Short"}
            </div>
          );
        },
      }),
      columnHelper.accessor("fillType", {
        header: (h) => <HeaderCell header={h}>Type</HeaderCell>,
        cell: (props) => {
          const value = props.getValue() as FillType;
          const { label, className } = getFillTypeDisplay(value);
          return (
            <div className={cn("font-medium text-xs", className)}>
              {label}
            </div>
          );
        },
      }),
      columnHelper.accessor("size", {
        header: (h) => (
          <HeaderCell header={h} align="right">
            Size
          </HeaderCell>
        ),
        cell: (props) => {
          const value = props.getValue();
          return (
            <div className="text-right font-medium">
              {formatCurrency(value, 4)}
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "tradeValue",
        header: (h) => (
          <HeaderCell header={h} align="right">
            Trade Value
          </HeaderCell>
        ),
        cell: (props) => {
          const trade = props.row.original;
          const value = trade.size * trade.exitPrice;
          
          return (
            <div className="text-right font-medium">
              ${formatCurrency(value, 2)}
            </div>
          );
        },
      }),
      columnHelper.accessor("exitPrice", {
        header: (h) => (
          <HeaderCell header={h} align="right">
            Price
          </HeaderCell>
        ),
        cell: (props) => (
          <PriceCell 
            price={props.getValue()} 
            market={props.row.original.market} 
          />
        ),
      }),
      columnHelper.accessor("fee", {
        header: (h) => (
          <HeaderCell header={h} align="right">
            Fee
          </HeaderCell>
        ),
        cell: (props) => {
          const value = props.getValue();
          return (
            <div className="text-right font-medium text-muted-foreground">
              ${formatCurrency(value, 2)}
            </div>
          );
        },
      }),
      columnHelper.accessor("pnl", {
        header: (h) => (
          <HeaderCell header={h} align="right">
            Closed PNL
          </HeaderCell>
        ),
        cell: (props) => {
          const value = props.getValue();
          const fillType = props.row.original.fillType;
          
          // For open positions, show dash since PNL isn't closed yet
          if (fillType === "open") {
            return (
              <div className="text-right font-medium text-muted-foreground">
                -
              </div>
            );
          }
          
          const isPositive = value >= 0;

          return (
            <div
              className={cn(
                "text-right font-medium",
                isPositive ? "text-bid-foreground" : "text-ask-foreground"
              )}
            >
              {isPositive ? "+" : "-"}${formatCurrency(Math.abs(value), 2)}
            </div>
          );
        },
      }),
    ],
    [setProduct, products, navigate, formatTradeDate]
  );

  return columns;
};

